import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'

import { ChevronRight } from '@mxenabled/mx-icons'

import { blocks, hasDrawerBlock } from '../mappings/blocks'

import Footer from './Footer'
import Header from './Header'
import FeedbackBar from './FeedbackBar'
import FeedbackDialog from './FeedbackDialog'
import DismissInsightDialog from './DismissInsightDialog'
import HiddenConfirmationCard from './HiddenConfirmationCard'
import { ObsolesceBanner, type ObsolesceBannerState } from './ObsolesceBanner'
import { useAppConfig, useGlobalCopyStore, useInsightsV2Store } from '../../common'
import { sendBeatCta } from '../../common/utils/PostMessage'
import type { Beat } from '../../core'
import { BeatActionsProvider, type BeatActions } from '../beatActions'
import { ActionBlockLayer } from '../actionBlocks/ActionBlockLayer'
import { useBeatFeedbackFlow } from '../hooks/useBeatFeedbackFlow'

export interface ObsoleteState {
  state: ObsolesceBannerState
  onRefresh: () => void
  onDismiss: () => void
}

interface BaseLayoutProps {
  beat: Beat
  beatActions?: BeatActions
  obsolete?: ObsoleteState
}

const NO_BEAT_ACTIONS: BeatActions = {}

const OBSOLETE_OVERLAY_SX = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'background.default',
  opacity: 0.7,
  zIndex: 1,
} as const

const BaseLayout = observer(
  ({ beat, beatActions = NO_BEAT_ACTIONS, obsolete }: BaseLayoutProps) => {
    const { openCtaDrawer } = useInsightsV2Store()
    const copy = useGlobalCopyStore()
    const { is_mobile_webview, clientConfig } = useAppConfig()
    const flow = useBeatFeedbackFlow(beat, beatActions)

    const insights_payload = beat.insights_payload

    if (!insights_payload) return null
    if (flow.cardState === 'dismissed') return null

    const {
      instance_slot,
      primary_cta,
      secondary_cta,
      sub_title,
      title,
      widget_type,
      guid,
      user_guid,
      dismissed_at,
    } = insights_payload
    const { block } = instance_slot

    // A CTA with a real drilldown `block` opens a drawer; otherwise it notifies
    // the host app via a beat/cta post message (the "Make a Transfer" CTA on the
    // Save an extra $100 beat). Mirrors Pulse's SaveAnExtra100Dollars beat.
    const handleCtaClick = (
      ctaBlock: string | null | undefined,
      source: 'primaryCta' | 'secondaryCta',
    ) => {
      if (hasDrawerBlock(ctaBlock)) {
        openCtaDrawer(guid, source)
      } else {
        sendBeatCta(beat, {
          isMobileWebview: is_mobile_webview,
          webviewScheme: clientConfig?.master?.ui_message_webview_url_scheme,
          userGuid: user_guid,
        })
      }
    }

    const Block = blocks[block as keyof typeof blocks]

    const isMini = widget_type === 'mini'
    const obsoleteCopy = copy.insights_feed?.general
    const isObsoleteDimmed =
      obsolete?.state === 'not_refreshable' && !dismissed_at && !!obsoleteCopy

    return (
      <BeatActionsProvider value={flow.wrappedActions}>
        <Stack sx={{ mb: 16 }}>
          {flow.cardState === 'confirming' ? (
            <HiddenConfirmationCard
              onClose={flow.collapseConfirmation}
              onLeaveFeedback={flow.openHideSurvey}
              onUndo={flow.undoDismiss}
            />
          ) : (
            <>
              {obsolete && obsoleteCopy && <ObsolesceBanner {...obsolete} copy={obsoleteCopy} />}
              <Card
                inert={isObsoleteDimmed}
                sx={{
                  ...(isObsoleteDimmed && {
                    position: 'relative',
                    zIndex: 2,
                    pointerEvents: 'none',
                  }),
                }}
              >
                {isObsoleteDimmed && <Box sx={OBSOLETE_OVERLAY_SX} />}
                <Header isMini={isMini} subtitle={sub_title} title={title ?? ''} />
                {Block && <Block beatGuid={guid} payload={insights_payload} />}

                <Stack sx={{ gap: 6, pb: 16, pl: 12, pr: 16, pt: 12 }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: insights_payload.description.html_text,
                    }}
                    style={{ paddingLeft: 4 }}
                  />
                  {/* TODO: Migrate to insights_payload.in_description_cta once backend provides it */}
                  {beat?.in_description_cta && (
                    <Button
                      endIcon={<ChevronRight />}
                      onClick={() => {}}
                      sx={{ height: 24, p: 4, width: 'max-content' }}
                    >
                      {beat.in_description_cta}
                    </Button>
                  )}
                </Stack>

                {!isMini && primary_cta?.label && (
                  <Stack sx={{ gap: 8, pb: 16, px: 16 }}>
                    <Button
                      onClick={() => handleCtaClick(primary_cta.block, 'primaryCta')}
                      variant="outlined"
                    >
                      {primary_cta.label}
                    </Button>
                    {secondary_cta?.label && (
                      <Button
                        color="secondary"
                        onClick={() => handleCtaClick(secondary_cta.block, 'secondaryCta')}
                      >
                        {secondary_cta.label}
                      </Button>
                    )}
                  </Stack>
                )}

                <FeedbackBar onTellUsMore={flow.openRatingSurvey} show={flow.isFeedbackBarShown} />
              </Card>
            </>
          )}

          <ActionBlockLayer beat={beat} />

          <DismissInsightDialog
            isOpen={flow.isDismissOpen}
            onClose={flow.closeDismissDialog}
            onDismiss={flow.confirmDismiss}
          />

          {flow.isSurveyOpen && (
            <FeedbackDialog
              characterLimitLabel={flow.survey.characterLimitLabel}
              closeAriaLabel={flow.survey.closeAriaLabel}
              isOpen={true}
              onBackClick={flow.feedbackDialogCallbacks.onBackClick}
              onClose={flow.closeSurvey}
              onSubmit={flow.submitSurvey}
              onTextInputFocus={flow.feedbackDialogCallbacks.onTextInputFocus}
              options={flow.survey.options}
              primaryText={flow.survey.submitText}
              secondaryText={flow.survey.backText}
              title={flow.survey.title}
            />
          )}

          {flow.cardState === 'visible' && insights_payload.footer && (
            <Footer footer={insights_payload.footer} />
          )}
        </Stack>
      </BeatActionsProvider>
    )
  },
)

export default BaseLayout
